import { useState, useEffect } from 'react';
import { expenseApi, expenseCategoryApi } from '../lib/api';
import { usePermissions } from '../components/Layout';
import {
  Plus,
  Search,
  Trash2,
  Edit,
  TrendingDown,
  Calendar,
  DollarSign,
  PieChart,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

// Catégories par défaut utilisées en fallback si l'API échoue
const DEFAULT_CATEGORIES = [
  { value: 'alimentaire', label: 'Alimentaire', icon: '🍽️', color: '#ff9800' },
  { value: 'electricite', label: 'Électricité', icon: '⚡', color: '#ffeb3b' },
  { value: 'eau', label: 'Eau', icon: '💧', color: '#2196f3' },
  { value: 'medical', label: 'Médical', icon: '🏥', color: '#f44336' },
  { value: 'dette_divers', label: 'Dettes diverses', icon: '📋', color: '#9c27b0' },
  { value: 'administratif', label: 'Administratif', icon: '📁', color: '#607d8b' },
];

const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

export default function ExpensesPage() {
  const { hasPermission } = usePermissions();
  const [expenses, setExpenses] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [editingExpense, setEditingExpense] = useState(null);

  const [newExpense, setNewExpense] = useState({
    category: 'alimentaire',
    amount: 0,
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
  };

  const getCategoryInfo = (categoryValue) => {
    const found = categories.find((c) => c.value === categoryValue);
    if (found) return found;
    // Fallback aux catégories par défaut si pas trouvé
    const defaultCat = DEFAULT_CATEGORIES.find((c) => c.value === categoryValue);
    return defaultCat || categories[0] || DEFAULT_CATEGORIES[0];
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') {
        params.category = filterCategory;
      }
      
      const [expensesRes, dashboardRes, categoriesRes] = await Promise.all([
        expenseApi.getAll(params),
        expenseApi.getDashboard({ year: selectedYear, month: selectedMonth ? parseInt(selectedMonth) : null }),
        expenseCategoryApi.getAll().catch(() => ({ data: [] })),
      ]);
      
      setExpenses(expensesRes.data);
      setDashboard(dashboardRes.data);
      
      // Utiliser les catégories de l'API si disponibles, sinon garder le fallback
      if (categoriesRes.data && categoriesRes.data.length > 0) {
        setCategories(categoriesRes.data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterCategory, selectedYear, selectedMonth]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!newExpense.description || !newExpense.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setFormLoading(true);
    try {
      if (editingExpense) {
        await expenseApi.update(editingExpense.id, newExpense);
        toast.success('Dépense mise à jour');
      } else {
        await expenseApi.create(newExpense);
        toast.success('Dépense enregistrée');
      }
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await expenseApi.delete(id);
      toast.success('Dépense supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setNewExpense({
      category: expense.category,
      amount: expense.amount,
      description: expense.description,
      expense_date: expense.expense_date,
      notes: expense.notes || '',
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setNewExpense({
      category: 'alimentaire',
      amount: 0,
      description: '',
      expense_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
  };

  const filteredExpenses = expenses.filter((e) =>
    e.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate chart data
  const chartData = dashboard?.by_category ? Object.entries(dashboard.by_category).map(([key, val]) => ({
    category: val.label,
    icon: val.icon,
    total: val.total,
    color: val.color,
    percentage: dashboard.total_expenses > 0 ? ((val.total / dashboard.total_expenses) * 100).toFixed(1) : 0,
  })).filter(d => d.total > 0) : [];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gestion des Dépenses</h1>
          <p className="text-muted-foreground">Suivi des dépenses par catégorie</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle dépense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Modifier la dépense' : 'Nouvelle dépense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select value={newExpense.category} onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}>
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Montant (FCFA) *</Label>
                <Input
                  type="number"
                  value={newExpense.amount || ''}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })}
                  placeholder="Ex: 25000"
                  data-testid="expense-amount"
                />
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  placeholder="Ex: Achat de riz pour la cantine"
                  data-testid="expense-description"
                />
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newExpense.expense_date}
                  onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
                  data-testid="expense-date"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Input
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                  placeholder="Informations complémentaires"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" disabled={formLoading} className="flex-1" data-testid="expense-submit-btn">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingExpense ? 'Modifier' : 'Enregistrer')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedMonth || "all"} onValueChange={(v) => setSelectedMonth(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Tous les mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {MONTHS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Dépenses</p>
                  <p className="text-xl font-bold text-red-600" data-testid="stat-total-expenses">{formatCurrency(dashboard.total_expenses)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <span className="text-xl">🍽️</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Alimentaire</p>
                  <p className="text-xl font-bold" data-testid="stat-alimentaire">{formatCurrency(dashboard.by_category?.alimentaire?.total || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <span className="text-xl">⚡</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Électricité</p>
                  <p className="text-xl font-bold" data-testid="stat-electricite">{formatCurrency(dashboard.by_category?.electricite?.total || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <span className="text-xl">💧</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Eau</p>
                  <p className="text-xl font-bold" data-testid="stat-eau">{formatCurrency(dashboard.by_category?.eau?.total || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      {dashboard && chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart Representation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Répartition par catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {chartData.map((item) => (
                  <div key={item.category} className="flex items-center gap-3">
                    <span className="text-xl w-8">{item.icon}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{item.category}</span>
                        <span className="font-medium">{formatCurrency(item.total)} ({item.percentage}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${item.percentage}%`, backgroundColor: item.color }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Évolution mensuelle ({selectedYear})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-40">
                {dashboard.by_month && Object.entries(dashboard.by_month).map(([month, data]) => {
                  const maxTotal = Math.max(...Object.values(dashboard.by_month).map(d => d.total || 0), 1);
                  const heightPercent = (data.total / maxTotal) * 100;
                  const monthLabel = MONTHS.find(m => month.endsWith(`-${m.value}`))?.label?.substring(0, 3) || month.split('-')[1];
                  return (
                    <div key={month} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-gray-100 rounded-t flex flex-col justify-end" style={{ height: '120px' }}>
                        <div
                          className="bg-primary/80 rounded-t transition-all hover:bg-primary"
                          style={{ height: `${heightPercent}%`, minHeight: data.total > 0 ? '4px' : '0' }}
                          title={`${monthLabel}: ${formatCurrency(data.total)}`}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{monthLabel}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Rechercher une dépense..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des dépenses ({filteredExpenses.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredExpenses.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Aucune dépense enregistrée</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => {
                  const catInfo = getCategoryInfo(expense.category);
                  return (
                    <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <TableCell className="text-muted-foreground">
                        {expense.expense_date}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className="border"
                          style={{ 
                            backgroundColor: catInfo.color ? catInfo.color + '20' : undefined,
                            borderColor: catInfo.color || undefined,
                            color: catInfo.color || undefined
                          }}
                        >
                          {catInfo.icon} {catInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{expense.description}</p>
                        {expense.notes && (
                          <p className="text-xs text-muted-foreground">{expense.notes}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-bold text-red-600">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(expense)} data-testid={`expense-edit-${expense.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)} className="text-red-600 hover:text-red-700" data-testid={`expense-delete-${expense.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
